import {
  useGetEnrollmentMultiSuitabilityData,
  useSubmitEnrollmentMultiSuitabilityData,
  type EnrollmentMultiSuitabilitySubmitInput,
} from '@egw/api'
import { Button, GenericError, Input, ModalDrawer, ProgressBar, RadioGroup, useModalState, Link } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { InformationCircleIcon as InfoIconOutline } from '@heroicons/react/24/outline'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Multi - Suitability')

interface FormData {
  isBrokerDealer?: number | undefined
  isDirectorPolicyMakerOfCompany?: number | undefined
  directorPolicyMakerOfCompany?: string | undefined
  isPoliticallyExposedPerson?: number | undefined
  affiliatedPoliticalOrganizations?: string | undefined
  hasPoliticallyExposedImmediateFamilyMembers?: number | undefined
  politicallyExposedImmediateFamilyMembers?: string | undefined
}

export const SuitabilityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { isOpen, openModal, closeModal } = useModalState()

  const { data, isLoading, error } = useGetEnrollmentMultiSuitabilityData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiSuitabilityData()
  const [hasFormInitialized, setHasFormInitialized] = useState(false)

  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.progress : undefined

  // Protect route
  useValidateRoute(data?.path)

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm<FormData>({
    defaultValues: {
      isBrokerDealer: undefined,
      isDirectorPolicyMakerOfCompany: undefined,
      directorPolicyMakerOfCompany: undefined,
      isPoliticallyExposedPerson: undefined,
      affiliatedPoliticalOrganizations: undefined,
      hasPoliticallyExposedImmediateFamilyMembers: undefined,
      politicallyExposedImmediateFamilyMembers: undefined,
    },
    mode: 'onChange',
  })

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.__typename === 'SubmitEnrollmentSuccess' && data.enrollmentData && !hasFormInitialized) {
      reset({
        isBrokerDealer:
          data.enrollmentData.isBrokerDealer === undefined ? undefined : data.enrollmentData.isBrokerDealer ? 1 : 0,
        isDirectorPolicyMakerOfCompany:
          data.enrollmentData.isDirectorPolicyMakerOfCompany === undefined
            ? undefined
            : data.enrollmentData.isDirectorPolicyMakerOfCompany
              ? 1
              : 0,
        directorPolicyMakerOfCompany: data.enrollmentData.directorPolicyMakerOfCompany || undefined,
        isPoliticallyExposedPerson:
          data.enrollmentData.investmentProfile.isPoliticallyExposedPerson === undefined
            ? undefined
            : data.enrollmentData.investmentProfile.isPoliticallyExposedPerson
              ? 1
              : 0,
        affiliatedPoliticalOrganizations:
          data.enrollmentData.investmentProfile.affiliatedPoliticalOrganizations?.join(', ') || undefined,
        hasPoliticallyExposedImmediateFamilyMembers:
          data.enrollmentData.investmentProfile.hasPoliticallyExposedImmediateFamilyMembers === undefined
            ? undefined
            : data.enrollmentData.investmentProfile.hasPoliticallyExposedImmediateFamilyMembers
              ? 1
              : 0,
        politicallyExposedImmediateFamilyMembers:
          data.enrollmentData.investmentProfile.politicallyExposedImmediateFamilyMembers?.join(', ') || undefined,
      })
      setHasFormInitialized(true)
    }
  }, [data, reset, hasFormInitialized])

  const isShowCompanyNameInput = watch('isDirectorPolicyMakerOfCompany')
  const isShowBrokerDealerInput = watch('isBrokerDealer')
  const showPoliticallyExposedPersonInput = watch('isPoliticallyExposedPerson')
  const showPoliticallyExposedPersonFamilyInput = watch('hasPoliticallyExposedImmediateFamilyMembers') === 1

  // Scroll to the input when the family members input is shown
  useEffect(() => {
    if (showPoliticallyExposedPersonFamilyInput) {
      const element = document.querySelector('input[name="politicallyExposedImmediateFamilyMembers"]')
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [showPoliticallyExposedPersonFamilyInput])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    const payload: EnrollmentMultiSuitabilitySubmitInput = {
      isBrokerDealer: Boolean(formData.isBrokerDealer),
      isDirectorPolicyMakerOfCompany: Boolean(formData.isDirectorPolicyMakerOfCompany),
      directorPolicyMakerOfCompany: formData.isDirectorPolicyMakerOfCompany
        ? formData.directorPolicyMakerOfCompany
        : undefined,

      investmentProfile: {
        isPoliticallyExposedPerson: Boolean(formData.isPoliticallyExposedPerson),
        affiliatedPoliticalOrganizations: formData.isPoliticallyExposedPerson
          ? formData.affiliatedPoliticalOrganizations?.split(',').map((org) => org.trim())
          : undefined,
        hasPoliticallyExposedImmediateFamilyMembers: Boolean(formData.hasPoliticallyExposedImmediateFamilyMembers),
        politicallyExposedImmediateFamilyMembers: formData.hasPoliticallyExposedImmediateFamilyMembers
          ? formData.politicallyExposedImmediateFamilyMembers?.split(',').map((member) => member.trim())
          : undefined,
      },
    }

    try {
      const response = await submit(payload)

      if (response?.path) {
        void navigate(response.path)
      } else if (!response?.path) {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.GENERIC.ROUTES.ERROR.path}`)
      }
    } catch (err) {
      log.error('Error submitting enrollment multi suitability data', err)
    }
  })

  return (
    <EnrollmentMultiLayout title={t('multi.suitability.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {/* AUTO GENERATED COMPONENTS START*/}
          <PageHeader title={t('multi.suitability.title')} className="mb-xs" />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-md" />
          <p className="typo-primary-base text-text-meta mb-xl">{t('multi.suitability.description')}</p>

          <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
            <p className="typo-primary-base-semibold mb-2xs">{t('multi.suitability.broker_dealer_title')}</p>
            <p className="typo-primary-base-regular text-text-meta mb-sm">
              {t('multi.suitability.broker_dealer_description')}
            </p>
            <Controller
              name="isBrokerDealer"
              control={control}
              rules={{
                required: true,
                validate: (value: unknown) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isBrokerDealer"
                  testId="is-broker-dealer-radio"
                  className="!py-xs"
                />
              )}
            />
            {isShowBrokerDealerInput ? (
              <p className="typo-primary-base-regular text-text-body mt-sm">
                {t('multi.suitability.is_yes_broker_dealer')}
              </p>
            ) : null}
            <p className="typo-primary-base-semibold mb-2xs mt-xl">{t('multi.suitability.major_share_holder_title')}</p>
            <p className="typo-primary-base-regular text-text-meta mb-sm">
              {t('multi.suitability.major_share_holder_description')}
            </p>
            <Controller
              name="isDirectorPolicyMakerOfCompany"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isDirectorPolicyMakerOfCompany"
                  testId="is-director-policy-maker-of-company-radio"
                  className="!py-xs"
                />
              )}
            />
            {isShowCompanyNameInput ? (
              <>
                <p className="typo-primary-base-regular text-text-body my-sm">
                  {t('multi.suitability.is_yes_major_share_holder')}
                </p>
                <Controller
                  name="directorPolicyMakerOfCompany"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('multi.suitability.company_name_and_ticker')}
                      name="directorPolicyMakerOfCompany"
                      testId="enrollment-name-company-input"
                      error={formErrors.directorPolicyMakerOfCompany?.message}
                    />
                  )}
                />
              </>
            ) : null}
            <p className="typo-primary-base-semibold mt-xl mb-2xs">
              {t('multi.suitability.politically_exposed_person_title')}
            </p>
            <div className="mb-sm">
              <button
                className="flex items-center typo-primary-base-semibold text-text-action py-2xs mb-sm"
                onClick={() => {
                  openModal()
                }}
                type="button"
              >
                <span>{t('multi.suitability.politically_exposed_person_description_1')}</span>{' '}
                <InfoIconOutline
                  strokeWidth={2.5}
                  onClick={openModal}
                  className="ml-3xs w-4 h-4 inline cursor-pointer "
                />
              </button>
              <p className="typo-primary-base-regular text-text-meta mb-2xs">
                {t('multi.suitability.politically_exposed_person_description_2')}
              </p>
            </div>
            {/* Politically Exposed Person */}
            <Controller
              name="isPoliticallyExposedPerson"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="isPoliticallyExposedPerson"
                  testId="is-politically-exposed-person_radio"
                  className="!pb-xs"
                />
              )}
            />
            {showPoliticallyExposedPersonInput ? (
              <>
                <p className="typo-primary-sm-semibold text-text-meta mb-3xs mt-2xs">
                  {t('multi.suitability.is_yes_politically_exposed_person')}
                </p>
                <Controller
                  name="affiliatedPoliticalOrganizations"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('multi.suitability.political_organizations')}
                      name="affiliatedPoliticalOrganizations"
                      testId="enrollment-name-company-input"
                      error={
                        touchedFields.affiliatedPoliticalOrganizations
                          ? formErrors.affiliatedPoliticalOrganizations?.message
                          : undefined
                      }
                      className="!pb-0"
                    />
                  )}
                />
              </>
            ) : null}

            {/* Family members yes/no */}
            <p className="typo-primary-base-regular text-text-meta mt-md mb-2xs">
              {t('multi.suitability.politically_exposed_person_family_description_1')}
            </p>
            <Controller
              name="hasPoliticallyExposedImmediateFamilyMembers"
              control={control}
              rules={{
                required: true,
                validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 0, label: t('common.no') },
                    { value: 1, label: t('common.yes') },
                  ]}
                  name="hasPoliticallyExposedImmediateFamilyMembers"
                  testId="is-politically-exposed-person-family_radio"
                  className="!py-xs"
                />
              )}
            />
            {/* Family members comma separated */}
            {showPoliticallyExposedPersonFamilyInput ? (
              <>
                <p className="typo-primary-sm-semibold text-text-meta mt-2xs mb-3xs">
                  {t('multi.suitability.is_yes_family_members')}
                </p>
                <Controller
                  name="politicallyExposedImmediateFamilyMembers"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('multi.suitability.family_members_placeholder')}
                      name="politicallyExposedImmediateFamilyMembers"
                      testId="politically-exposed-immediate-family-members-input"
                      error={
                        touchedFields.politicallyExposedImmediateFamilyMembers
                          ? formErrors.politicallyExposedImmediateFamilyMembers?.message
                          : undefined
                      }
                      isPII
                    />
                  )}
                />
              </>
            ) : null}

            <p className="typo-primary-sm-light text-text-meta mt-sm mb-xl">{t('multi.suitability.attestment')}</p>
            <Button
              type="submit"
              className="w-full md:w-min"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="enrollment-continue-button"
            >
              {t('common.continue')}
            </Button>
          </form>
          {/* AUTO GENERATED COMPONENTS END*/}
        </div>
      ) : null}

      <ModalDrawer title={t('multi.suitability.modal_title')} isOpen={isOpen} onClose={closeModal}>
        <div className="typo-primary-base-light text-text-body">
          <p>{t('multi.suitability.modal_description_1')}</p>
          <ul>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_1')}</li>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_2')}</li>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_3')}</li>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_4')}</li>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_5')}</li>
            <li className="list-disc list-inside ml-2">{t('multi.suitability.modal_ul_6')}</li>
          </ul>
          <p className="mt-md">{t('multi.suitability.modal_description_2')}</p>
        </div>
      </ModalDrawer>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
